/**
 * Pay-and-reactivate for expired monthly Mobile accounts (PQUser "Renewals").
 *
 *   POST /account/me/reactivation/quote                 -> quote to review; no charge
 *   POST /account/me/reactivation/{quoteId}/confirm     -> charges the saved card (Idempotency-Key)
 *   GET  /account/me/reactivation/{quoteId}             -> recorded status; never charges
 *
 * Quote response (confirmed against the API):
 *   { quoteId, status: "Quoted", amount: 79.99, currency: "USD", newEndDate: "2026-10-28",
 *     expiresAtUtc: "2026-09-28T19:10:26.1643822Z", cardLast4, cardType, transactionId: null }
 * Confirm/read responses are assumed to share this shape with status "Completed" and a transactionId;
 * other status names are mapped conservatively. Both parsers fail closed: an unrecognised response
 * is never treated as payable or completed.
 */
import type { PqPaymentInfo, ProblemDetails } from './pq-client';

export interface ReactivationQuote {
    quoteId: string;
    /** Server-calculated amount in major units (e.g. 49.99). */
    amount: number;
    currency: string;
    card: Pick<PqPaymentInfo, 'cardType' | 'cardLast4' | 'expiryMonth' | 'expiryYear'> | null;
    /** New subscription end date (ISO 8601). */
    newEndDate: string;
    /** When the quote stops being confirmable (ISO 8601). */
    expiresAt: string;
}

export type AttemptStatus = 'Completed' | 'Unconfirmed' | 'InProgress' | 'Failed' | 'Unknown';

export interface ReactivationAttempt {
    quoteId: string;
    status: AttemptStatus;
    transactionReference: string | null;
    message: string | null;
}

/** The quote endpoint's "can't be done here" answer for ineligible or special-billing accounts. */
export interface SupportRequired {
    supportRequired: true;
    message: string | null;
}

const text = (value: unknown) => (typeof value === 'string' && value.trim() ? value.trim() : null);
/**
 * Parses an ISO timestamp. .NET sends up to 7 fractional-second digits, but JavaScript only guarantees
 * 3 (Safari is stricter than V8), so extra digits are trimmed to milliseconds first.
 */
export function parseTimestamp(value: unknown): number | null {
    if (typeof value !== 'string') return null;
    const time = Date.parse(value.trim().replace(/(\.\d{3})\d+/, '$1'));
    return Number.isNaN(time) ? null : time;
}
const isDate = (value: unknown) => parseTimestamp(value) !== null;

export function parseQuote(data: unknown): ReactivationQuote | SupportRequired | null {
    if (!data || typeof data !== 'object') return null;
    const body = data as Record<string, unknown>;
    if (body.supportRequired === true || body.status === 'SupportRequired') {
        return { supportRequired: true, message: text(body.message) ?? text(body.detail) };
    }
    // Only an open quote can be paid; any other status on this endpoint is unexpected.
    if (body.status !== undefined && body.status !== 'Quoted') return null;
    const quoteId = text(body.quoteId);
    const amount = typeof body.amount === 'number' && Number.isFinite(body.amount) && body.amount > 0 ? body.amount : null;
    const currency = text(body.currency)?.toUpperCase() ?? null;
    const expiresAt = body.expiresAtUtc ?? body.expiresAt;
    if (!quoteId || amount === null || !currency || !/^[A-Z]{3}$/.test(currency) || !isDate(body.newEndDate) || !isDate(expiresAt)) return null;
    const cardLast4 = text(body.cardLast4);
    const card = cardLast4
        ? { cardLast4, cardType: text(body.cardType), expiryMonth: null, expiryYear: null }
        : null;
    return { quoteId, amount, currency, card, newEndDate: body.newEndDate as string, expiresAt: expiresAt as string };
}

const STATUSES: Record<string, AttemptStatus> = {
    completed: 'Completed',
    unconfirmed: 'Unconfirmed', quoted: 'Unconfirmed', expired: 'Unconfirmed',
    pending: 'InProgress', processing: 'InProgress', inprogress: 'InProgress', submitted: 'InProgress',
    failed: 'Failed', declined: 'Failed',
};

export function parseAttempt(data: unknown, quoteId: string): ReactivationAttempt | null {
    if (!data || typeof data !== 'object') return null;
    const body = data as Record<string, unknown>;
    const raw = text(body.status);
    if (!raw) return null;
    return {
        quoteId: text(body.quoteId) ?? quoteId,
        status: STATUSES[raw.toLowerCase().replace(/[^a-z]/g, '')] ?? 'Unknown',
        transactionReference: text(body.transactionReference) ?? text(body.transactionId),
        message: text(body.message) ?? text(body.detail),
    };
}

/** What the review screen should offer after a failed renewal request. */
export interface ReactivationFailure {
    message: string;
    /**
     * new-quote: nothing was charged; request a fresh quote.
     * check-status: the outcome is unknown; look it up or retry with the SAME quote and key.
     * support / sign-in: stop.
     */
    next: 'new-quote' | 'check-status' | 'support' | 'sign-in';
}

export function describeReactivationFailure(stage: 'quote' | 'confirm', status: number, problem: ProblemDetails | null): ReactivationFailure {
    const detail = text(problem?.detail);
    const type = (problem?.type ?? '').toLowerCase();
    if (status === 401) return { message: 'Your session has expired. Sign in again to continue.', next: 'sign-in' };
    if (status === 403 || type.includes('support')) {
        return { message: detail ?? "Your subscription can't be renewed in the app. Please contact ParcelQuest support.", next: 'support' };
    }
    // No response or a server error while confirming: the card may have been charged.
    if (stage === 'confirm' && (status === 0 || status >= 500 || type.includes('in-progress'))) {
        return { message: "We couldn't confirm the payment result. Check the status before trying again; you won't be charged twice.", next: 'check-status' };
    }
    if (type.includes('expired')) return { message: 'This quote has expired. Review a new quote to continue.', next: 'new-quote' };
    if (status === 402 || type.includes('declined')) {
        return { message: `Your card was declined${detail ? `: ${detail}` : '.'} Replace your card, then review a new quote.`, next: 'new-quote' };
    }
    if (type.includes('rejected')) return { message: detail ?? 'The payment was not accepted. Review a new quote to try again.', next: 'new-quote' };
    if (stage === 'quote' && (status === 0 || status >= 500)) {
        return { message: 'Unable to load your renewal. Check your connection and try again.', next: 'new-quote' };
    }
    return { message: detail ?? 'Your subscription could not be renewed here. Please contact ParcelQuest support.', next: 'support' };
}

export function formatAmount(amount: number, currency: string): string {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
}

/**
 * Formats a calendar date such as newEndDate ("2026-10-28"). Date-only strings parse as UTC midnight,
 * which would show the previous day in U.S. time zones, so they are formatted in UTC.
 */
export function formatDate(value: string): string {
    const time = parseTimestamp(value);
    if (time === null) return value;
    const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value.trim());
    return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', ...(dateOnly && { timeZone: 'UTC' }) }).format(time);
}

/** Formats an instant (e.g. the quote expiry) as a local time like "12:10 PM". */
export function formatTime(value: string): string {
    const time = parseTimestamp(value);
    return time === null ? value : new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(time);
}

export const isQuoteExpired = (quote: Pick<ReactivationQuote, 'expiresAt'>, now = Date.now()) => (parseTimestamp(quote.expiresAt) ?? 0) <= now;

/**
 * The only renewal state kept across reloads: the identifiers needed to recover a submitted
 * confirmation. Scoped to the account and removed once the outcome is known or on sign-out.
 */
export interface PendingConfirmation {
    quoteId: string;
    idempotencyKey: string;
}

const storageKey = (userId: string) => `pq-reactivation:${userId}`;

export function loadPendingConfirmation(userId: string, storage: Pick<Storage, 'getItem'> | undefined = globalThis.localStorage): PendingConfirmation | null {
    try {
        const value = JSON.parse(storage?.getItem(storageKey(userId)) ?? 'null');
        return value && typeof value.quoteId === 'string' && typeof value.idempotencyKey === 'string'
            ? { quoteId: value.quoteId, idempotencyKey: value.idempotencyKey } : null;
    } catch {
        return null;
    }
}

export function savePendingConfirmation(userId: string, pending: PendingConfirmation, storage: Pick<Storage, 'setItem'> | undefined = globalThis.localStorage): void {
    try { storage?.setItem(storageKey(userId), JSON.stringify(pending)); } catch { /* recovery falls back to the in-memory copy */ }
}

export function clearPendingConfirmation(userId: string, storage: Pick<Storage, 'removeItem'> | undefined = globalThis.localStorage): void {
    try { storage?.removeItem(storageKey(userId)); } catch { /* nothing to clear */ }
}

/** Removes every account's saved recovery identifiers (sign-out). */
export function clearAllPendingConfirmations(storage: Storage | undefined = globalThis.localStorage): void {
    try {
        for (let i = (storage?.length ?? 0) - 1; i >= 0; i--) {
            const key = storage?.key(i);
            if (key?.startsWith('pq-reactivation:')) storage?.removeItem(key);
        }
    } catch { /* storage unavailable */ }
}
